import {
  getLocale,
  SALESFORCE_LOCALES_MAP,
} from "@devforce/trailhead-client-locale";
import { Product } from "common/learnerPreferences";

interface UrlFormatter {
  /**
   * @returns formatted url.
   */
  format(): string;
}

/**
 * The simplest formatter; just returns the url passed in constructor.
 */
class SimpleFormatter implements UrlFormatter {
  private url: string;

  constructor(url: string) {
    this.url = url;
  }

  format() {
    return this.url;
  }
}

/**
 * Formatter for products in https://help.salesforce.com/s/articleView?id=.
 */
class HelpArticleFormatter implements UrlFormatter {
  private articleId;
  private static baseUrl = "https://help.salesforce.com/s/articleView?id=";

  constructor(articleId: string) {
    this.articleId = articleId;
  }

  format() {
    const locale = getLocale() as keyof typeof SALESFORCE_LOCALES_MAP;
    const sfdcLocale = SALESFORCE_LOCALES_MAP[locale];
    const localeQuery = sfdcLocale ? `&language=${sfdcLocale}` : "";
    return `${HelpArticleFormatter.baseUrl}${this.articleId}&type=5${localeQuery}`;
  }
}

/**
 * Formatter for products in https://help.salesforce.com/s/products.
 */
class SfdcFormatter implements UrlFormatter {
  private productPath;
  private static baseUrl = "https://help.salesforce.com/s/products";

  constructor(productPath: string) {
    this.productPath = productPath;
  }

  format() {
    const locale = getLocale() as keyof typeof SALESFORCE_LOCALES_MAP;
    const sfdcLocale = SALESFORCE_LOCALES_MAP[locale];
    const query = sfdcLocale ? `?language=${sfdcLocale}` : "";
    return `${SfdcFormatter.baseUrl}${this.productPath}${query}`;
  }
}

/**
 * Formatter for Tableau, which has a different site and locale format.
 */
class TableauFormatter implements UrlFormatter {
  private static localeMap: Record<string, string> = {
    en: "",
    de: "de-de",
    es: "es-es",
    "es-MX": "es-es",
    fr: "fr-fr",
    it: "it-it",
    ja: "ja-jp",
    ko: "ko-kr",
    "pt-BR": "pt-br",
    "zh-CN": "zh-cn",
  };

  format() {
    const locale = getLocale();
    const tableauLocale = TableauFormatter.localeMap[locale];
    const localePath = tableauLocale ? `/${tableauLocale}` : "";
    return `https://www.tableau.com${localePath}/support/help`;
  }
}

// Maps product to its url formatter.
const productUrlFormatters: Partial<Record<Product, UrlFormatter>> = {
  [Product.AppExchange]: new SimpleFormatter(
    "https://appexchange.salesforce.com/learn"
  ),
  [Product.CommerceCloud]: new SfdcFormatter("/commerce"),
  [Product.ConsumerGoodsCloud]: new SimpleFormatter("/consumergoods"),
  [Product.CRMAnalytics]: new SfdcFormatter("/analytics"),
  [Product.DataCloud]: new SfdcFormatter("/datacloud"),
  [Product.EducationCloud]: new HelpArticleFormatter(
    "sfdo.Education_Cloud.htm"
  ),
  [Product.Einstein]: new SfdcFormatter("/einstein"),
  [Product.ExperienceCloud]: new SfdcFormatter("/experience"),
  [Product.FinancialServicesCloud]: new SfdcFormatter("/financialservices"),
  [Product.FlowBuilder]: new HelpArticleFormatter("sf.flow.htm"),
  [Product.HealthCloud]: new SfdcFormatter("/health"),
  [Product.LoyaltyManagement]: new HelpArticleFormatter(
    "sf.loyaltyoverview.htm"
  ),
  [Product.ManufacturingCloud]: new SfdcFormatter("/manufacturing"),
  [Product.MarketingCloud]: new SfdcFormatter("/marketing"),
  [Product.MuleSoft]: new SimpleFormatter("https://help.mulesoft.com/s/"),
  [Product.NetZeroCloud]: new HelpArticleFormatter(
    "release-notes.rn_netzero.htm&release=250"
  ),
  [Product.NonprofitCloud]: new SfdcFormatter("/nonprofit"),
  [Product.PublicSectorSolutions]: new HelpArticleFormatter(
    "sf.psc_admin_concept_psc_welcom.htm"
  ),
  [Product.SalesCloud]: new SfdcFormatter("/sales"),
  [Product.SalesforcePlatform]: new SfdcFormatter("/platform"),
  [Product.ServiceCloud]: new SfdcFormatter("/service"),
  [Product.Slack]: new SimpleFormatter("https://slack.com/help"),
  [Product.Tableau]: new TableauFormatter(),
};

/**
 * Returns formatted help url for given product.
 * @param product - product
 */
export function getHelpUrl(product: Product) {
  return productUrlFormatters[product]?.format();
}

const baseUrlFormatter = new SfdcFormatter("");

/**
 * Returns formatted base help url.
 */
export function getBaseHelpUrl() {
  return baseUrlFormatter.format();
}
